import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Select,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import ResourceCount from '../../../components/resource-count-text/ResourceCount';
import { usePermissions } from '../../../hooks/usePermissions';
import { State } from '../../../types/markets/types';
import { isExternalMarket } from '../../../utils/externalEntity';
import MarketPlansList from './MarketPlansList';
import MarketPracticeList from './MarketPracticeList';
import MarketProviderList from './MarketProviderList';

const StateDetailPage = () => {
  const navigate = useNavigate();
  const { permissions, isReady } = usePermissions();
  const { stateName } = useParams<{ stateName: string }>();
  const [stateData, setStateData] = useState<State | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedMarkets, setExpandedMarkets] = useState(
    new Set<string>()
  );
  const [marketResources, setMarketResources] = useState<
    Record<string, string>
  >({});

  const buttonBg = useColorModeValue(
    'background.secondary',
    'gray.700'
  );
  const buttonHoverBg = useColorModeValue('gray.300', 'gray.600');
  const selectBorderColor = 'background.secondary';
  const [sortOrder, setSortOrder] = useState('az');
  // const [selectedResource, setSelectedResource] =
  //   useState('Practices');

  useEffect(() => {
    if (!isReady) return;

    if (!stateName) {
      console.error('No state name provided in URL.');
      return;
    }

    const adminFlag = permissions.isAdmin;
    const cachedStates = localStorage.getItem('statesData');
    if (cachedStates) {
      try {
        const states: State[] = JSON.parse(cachedStates);
        const state = states.find((s) => s.name === stateName);
        if (state) {
          const visibleMarkets = adminFlag
            ? state.markets
            : state.markets.filter(
                (market) => !isExternalMarket(market)
              );

          const sortedMarkets = [...visibleMarkets].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setStateData({
            ...state,
            markets: sortedMarkets,
            practiceCount: sortedMarkets.reduce(
              (count, market) => count + (market.practiceCount || 0),
              0
            ),
            providerCount: sortedMarkets.reduce(
              (count, market) => count + (market.providerCount || 0),
              0
            ),
            plansCount: sortedMarkets.reduce(
              (count, market) => count + (market.plansCount || 0),
              0
            ),
          });
        } else {
          console.error(`State with name "${stateName}" not found.`);
        }
      } catch (error) {
        console.error(
          'Error parsing states from localStorage:',
          error
        );
      }
    } else {
      console.error('No states data found in localStorage.');
    }

    setLoading(false);
  }, [isReady, permissions.isAdmin, stateName]);

  const toggleMarket = (marketId: string) => {
    const newExpandedMarkets = new Set(expandedMarkets);
    if (newExpandedMarkets.has(marketId)) {
      newExpandedMarkets.delete(marketId);
    } else {
      newExpandedMarkets.add(marketId);
      if (!marketResources[marketId]) {
        setMarketResources((prev) => ({
          ...prev,
          [marketId]: 'Practices',
        }));
      }
    }
    setExpandedMarkets(newExpandedMarkets);
  };

  const toggleAllMarkets = (expand: boolean) => {
    if (stateData) {
      if (expand) {
        const allmarketId = stateData.markets.map(
          (market) => market.id
        );
        setExpandedMarkets(new Set(allmarketId));

        const defaultResources = Object.fromEntries(
          allmarketId.map((id) => [
            id,
            marketResources[id] || 'Practices',
          ])
        );
        setMarketResources(defaultResources);
      } else {
        setExpandedMarkets(new Set());
      }
    }
  };

  const handleResourceChange = (
    marketId: string,
    resource: string
  ) => {
    setMarketResources((prev) => ({
      ...prev,
      [marketId]: resource,
    }));
  };
  const sortMarkets = (order: string) => {
    if (stateData) {
      const sortedMarkets = [...stateData.markets].sort((a, b) =>
        order === 'az'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name)
      );
      setStateData({ ...stateData, markets: sortedMarkets });
      setSortOrder(order);
    }
  };

  const renderResourceComponent = (
    marketId: string,
    resource: string
  ) => {
    switch (resource) {
      case 'Practices':
        return <MarketPracticeList marketId={[marketId]} />;
      case 'Providers':
        return <MarketProviderList marketId={[marketId]} />;
      case 'Plans':
        return <MarketPlansList marketId={[marketId]} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" mt="4rem">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (!stateData) {
    return <Text textAlign="center">State data not found.</Text>;
  }

  return (
    <>
      <Helmet>
        <title>{stateData.name} Details</title>
      </Helmet>
      <Box
        mt="1.4rem"
        mb="2.38rem"
        pl="2.06rem"
        pr="2.06rem"
        pb="2.13rem"
      >
        <Text fontSize="2xl" fontWeight="bold" mb="4">
          {stateData.name}
        </Text>
        <HStack
          justify={'space-between'}
          mb="1.5rem"
          alignItems={'center'}
        >
          <HStack spacing={4}>
            <Text>Markets: {stateData.markets.length}</Text>
            <Text>Practices: {stateData.practiceCount}</Text>
            <Text>Providers: {stateData.providerCount}</Text>
            <Text>Plans: {stateData.plansCount}</Text>
          </HStack>
          <HStack gap="0.75rem">
            <Button
              bg={buttonBg}
              height={'2.5rem'}
              _hover={{ bg: buttonHoverBg }}
              _active={{
                bg: buttonHoverBg,
                transform: 'scale(0.98)',
              }}
              _focus={{ boxShadow: 'outline' }}
              padding="0 1rem"
              borderRadius={'0.375rem'}
              border="none"
              onClick={() =>
                expandedMarkets.size === stateData.markets.length
                  ? toggleAllMarkets(false)
                  : toggleAllMarkets(true)
              }
            >
              <Text
                fontSize={'1rem'}
                fontWeight={500}
                lineHeight={'1.5rem'}
                color="text.primary"
              >
                {expandedMarkets.size === stateData.markets.length
                  ? 'Collapse all -'
                  : 'Expand all +'}
              </Text>
            </Button>

            <Select
              bg={buttonBg}
              width="10.375rem"
              height={'2.5rem'}
              _hover={{ bg: buttonHoverBg }}
              _active={{
                bg: buttonHoverBg,
                transform: 'scale(0.98)',
              }}
              _focus={{ boxShadow: 'outline' }}
              borderRadius={'0.375rem'}
              border="none"
              value={sortOrder}
              onChange={(e) => sortMarkets(e.target.value)}
            >
              <option value="az">A - Z</option>
              <option value="za">Z - A</option>
            </Select>
          </HStack>
        </HStack>
        <VStack align="stretch" mt="4" spacing={4}>
          {stateData.markets.map((market) => (
            <Box
              key={market.id}
              border="1px"
              borderColor={selectBorderColor}
              borderRadius="md"
              p="1rem"
            >
              <HStack justify={'space-between'}>
                <HStack gap={'1rem'}>
                  <Tooltip label={market.name}>
                    <Text
                      color="background.brandLight"
                      fontSize="1rem"
                      fontWeight={500}
                      lineHeight={'120%'}
                      cursor={'pointer'}
                      onClick={() =>
                        navigate(`/markets/${market.id}`)
                      }
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {market.name}
                    </Text>
                  </Tooltip>
                  {permissions.isAdmin && (market.external ? (
                    <Flex
                      justify={'center'}
                      alignItems={'center'}
                      px="0.625rem"
                      py="0.25rem"
                      borderRadius={'0.375rem'}
                      background="orange.100"
                      border="1px solid"
                      borderColor="orange.300"
                      cursor="default"
                    >
                      <Text
                        color="orange.800"
                        fontSize={'0.75rem'}
                        fontWeight={600}
                        lineHeight={'1rem'}
                      >
                        External
                      </Text>
                    </Flex>
                  ) : market.tablespace ? (
                    <Tooltip
                      label={`Tablespace: ${market.tablespace}`}
                      hasArrow
                    >
                      <Flex
                        justify={'center'}
                        alignItems={'center'}
                        px="0.625rem"
                        py="0.25rem"
                        borderRadius={'0.375rem'}
                        background={'background.green100'}
                        border="1px solid"
                        borderColor="green.300"
                        cursor="default"
                      >
                        <Text
                          color="green.800"
                          fontSize={'0.75rem'}
                          fontWeight={600}
                          lineHeight={'1rem'}
                        >
                          {market.tablespace}
                        </Text>
                      </Flex>
                    </Tooltip>
                  ) : null)}
                  <HStack gap="0.38rem">
                    <ResourceCount
                      count={market.practiceCount}
                      resource="Practices"
                      isSelected={
                        marketResources[market.id] === 'Practices'
                      }
                      onClick={() =>
                        handleResourceChange(market.id, 'Practices')
                      }
                    />
                    <ResourceCount
                      count={market.providerCount}
                      resource="Providers"
                      isSelected={
                        marketResources[market.id] === 'Providers'
                      }
                      onClick={() =>
                        handleResourceChange(market.id, 'Providers')
                      }
                    />
                    <ResourceCount
                      count={market.plansCount}
                      resource="Plans"
                      isSelected={
                        marketResources[market.id] === 'Plans'
                      }
                      onClick={() =>
                        handleResourceChange(market.id, 'Plans')
                      }
                    />
                  </HStack>
                </HStack>
                <Button
                  size="sm"
                  onClick={() => toggleMarket(market.id)}
                  variant="link"
                  colorScheme="blue"
                >
                  <Flex
                    gap="0.5rem"
                    alignItems={'center'}
                    justifyContent={'center'}
                  >
                    <Text>
                      {expandedMarkets.has(market.id)
                        ? 'Collapse'
                        : 'Expand'}
                    </Text>
                    {expandedMarkets.has(market.id) ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                  </Flex>
                </Button>
              </HStack>
              <Collapse
                in={expandedMarkets.has(market.id)}
                animateOpacity
              >
                <Box mt="4">
                  {renderResourceComponent(
                    market.id,
                    marketResources[market.id]
                  )}
                </Box>

                {/* <Box mt="1rem">
                  <Text>Location: {market.location || 'N/A'}</Text>
                  <Text>Resource Links:</Text>
                  <VStack align="start" mt="1rem">
                    {market.resource &&
                      Object.entries(market.resource).map(
                        ([key, value]) => (
                          <Text key={key}>
                            {key}:{' '}
                            {typeof value === 'string'
                              ? value
                              : JSON.stringify(value)}
                          </Text>
                        )
                      )}
                  </VStack>
                </Box> */}
              </Collapse>
            </Box>
          ))}
        </VStack>
      </Box>
    </>
  );
};

export default StateDetailPage;
